import { Button, ConfigProvider, Form, Modal } from 'antd';
import cls from 'classnames';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useMemo } from 'react';

import { loadingIcon, retryIcon } from '../../assets';
import { noDataIcon, resetWithoutDataIcon } from '../../assets/MetaArea';
import {
  setNpcSettings,
  setPersonaLogicModalVisibility,
  setUseNpcSettings,
  SuggestNpcSetting,
  useAppDispatch,
  useAppSelector,
} from '../../state';
import { track } from '../../track';
import { NpcSettingSuggestScene } from '../../types/meta';
import { isTalkie } from '../../utils';
import { PAGE } from '../EditHeader';
import { ENpcStatusTrackText } from '../Home';
import { getNpcStatus } from '../Home/NpcList';
import { LocalImage } from '../Image';
import { ITextArea } from '../Textarea';

interface IPersonaLogicModalProps {
  loading?: boolean;
  styles: Record<string, string>;
  isOpen: boolean;
}

const { Item } = Form;

const theme = {
  token: {
    colorBgContainer: '#FFFFFF0A',
    colorBorder: 'transparent',
    colorTextPlaceholder: '#FFFFFF59',
    colorText: '#FFFFFFE5',
    colorTextTertiary: '#FFFFFF59',
    borderRadius: 8,
    lineHeight: 1.5,
    colorSplit: '#F0F0F51A',
    colorBgMask: '#000000B2',
  },
  components: {
    Input: {
      paddingBlock: 12,
      paddingInline: 16,
      inputFontSize: 16,
    },
    Form: {
      labelColor: '#FFFFFFE5',
      labelFontSize: 16,
      labelHeight: 22,
      labelRequiredMarkColor: '#FFFFFF59',
      verticalLabelPadding: '0 0 16px',
      itemMarginBottom: 28,
    },
  },
};

const customizeRequiredMark =
  (styles: Record<string, string>) =>
  (label: React.ReactNode, { required }: { required: boolean }) => (
    <>
      <span>{label}</span>
      {required && <span className={styles.requiredMark}>*</span>}
    </>
  );

export function PersonaLogicModal(props: IPersonaLogicModalProps) {
  const { t } = useTranslation();

  const { styles, isOpen } = props;
  const [form] = Form.useForm();

  const dispatch = useAppDispatch();
  const {
    npcSettingsLoading: loading,
    npcSettings,
    npcId,
    statusInfo,
  } = useAppSelector((state) => state.edit);
  const status = getNpcStatus(statusInfo);

  const isNoEmptyObject = useMemo(() => Object.keys(npcSettings).length > 0, [npcSettings]);

  const requiredMark = useMemo(() => customizeRequiredMark(styles), [styles]);

  useEffect(() => {
    form.setFieldsValue(npcSettings);
  }, [form, npcSettings]);

  const handleValuesChange = async (changedValues: any) => {
    dispatch(setNpcSettings(changedValues));
  };

  const handleReset = () => {
    if (loading) return;

    track.send('ai_write_popup_click', PAGE, {
      npc_id: npcId,
      ugc_npc_approving_status: ENpcStatusTrackText[status],
      ai_write_click_position: 'persona_and_prompt',
      ai_write_popup_click_type: 'retry',
    });

    form.resetFields();
    dispatch(setNpcSettings(null));
    dispatch(SuggestNpcSetting({ params: NpcSettingSuggestScene.advanced }));
    form.resetFields();
  };

  const onCancel = () => {
    // 重试:retry、保存:save、取消：cancel
    track.send('ai_write_popup_click', PAGE, {
      npc_id: npcId,
      ugc_npc_approving_status: ENpcStatusTrackText[status],
      ai_write_click_position: 'persona_and_prompt',
      ai_write_popup_click_type: 'cancel',
    });

    dispatch(setNpcSettings(null));
    dispatch(setPersonaLogicModalVisibility(false));
    form.resetFields();
  };

  const handleApply = () => {
    // 重试:retry、保存:save、取消：cancel
    track.send('ai_write_popup_click', PAGE, {
      npc_id: npcId,
      ugc_npc_approving_status: ENpcStatusTrackText[status],
      ai_write_click_position: 'persona_and_prompt',
      ai_write_popup_click_type: 'save',
    });

    dispatch(setUseNpcSettings(true));
    onCancel();
  };

  const renderLoading = () => (
    <div className={styles.loading}>
      <div className={styles.loadingContent}>
        <LocalImage
          width="40"
          height="40"
          alt="loading icon"
          src={loadingIcon}
          className={styles.loadingIcon}
        />
        <p className={styles.loadingText}>{t('optimizing')}</p>
      </div>
    </div>
  );

  const renderForm = () => (
    <Form
      name="metaInfo"
      form={form}
      layout="vertical"
      validateTrigger="onBlur"
      requiredMark={requiredMark}
      className={styles.personaLogicForm}
      onValuesChange={handleValuesChange}
    >
      <Item name="long_desc">
        {/* 附加设定 = 2000字符（1000汉字）； */}
        <ITextArea
          minRows={12}
          wrapperCls={styles.longTextArea}
          placeholder={t('long_desc_placeholder_old')}
          maxLength={isTalkie ? 2000 : 1000}
          showCount
        />
      </Item>
    </Form>
  );

  const renderNoData = () => (
    <div className={styles.empty}>
      <LocalImage width="100" height="82" alt="no data" src={noDataIcon} />
      <p className={styles.noDataText}>{t('auto_generate_failed')}</p>
      <div className={styles.retry} onClick={handleReset}>
        {t('retry')}
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <ConfigProvider theme={theme}>
      <Modal
        centered
        title={null}
        footer={null}
        open={isOpen}
        width="500px"
        destroyOnClose
        closable={false}
        onCancel={onCancel}
        wrapClassName={styles.infoModal}
      >
        <div className={styles.modalHeader}>
          <h1 className={styles.title}>{t('persona_logic')}</h1>
          <section
            className={cls(styles.reset, { [styles.resetUnData]: !isNoEmptyObject || loading })}
            onClick={handleReset}
          >
            <span>{t('retry')}</span>
            <LocalImage
              width="20"
              height="20"
              alt="reset icon"
              src={!isNoEmptyObject || loading ? resetWithoutDataIcon : retryIcon.src}
            />
          </section>
        </div>

        {loading ? renderLoading() : isNoEmptyObject ? renderForm() : renderNoData()}

        <footer className={styles.footer}>
          <Button className={styles.cancelButton} onClick={onCancel}>
            {t('cancel')}
          </Button>
          <button
            onClick={handleApply}
            disabled={!isNoEmptyObject || loading}
            className={styles.confirmButton}
          >
            {t('apply')}
          </button>
        </footer>
      </Modal>
    </ConfigProvider>
  );
}

export default PersonaLogicModal;
